import { useSearchParams } from "react-router-dom";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import MultiSelectTags from "./multi-select-tags";
import {
  professionalStatusLabels,
  jobCommitmentLabels,
  ProfessionalType,
  ProfessionalStatus,
} from "../schema/professional-schema";
import { SupervisorOption } from "../hooks/use-professionals";
import { useEffect, useMemo } from "react";

// Define the filter form interface
interface ProfessionalFilters {
  status: string;
  job_commitment: string;
  type: string;
  qualification: string[];
  is_supervisor: string;
  is_lead: string;
  supervisor_id: string;
}

// A supervisor option: non-active supervisors stay listed (their reports
// still need finding) but say so, so nobody mistakes them for current staff.
function PersonOption({ person }: { person: SupervisorOption }) {
  return (
    <>
      {person.name}
      {person.status !== ProfessionalStatus.ACTIVE && (
        <span className="text-muted-foreground">
          {" · "}
          {professionalStatusLabels[person.status as ProfessionalStatus] ??
            person.status}
        </span>
      )}
    </>
  );
}

// Filters stored in the URL as a plain value, with "ALL" meaning "not set".
const SINGLE_VALUE_FILTERS = [
  "status",
  "job_commitment",
  "type",
  "is_supervisor",
  "is_lead",
  "supervisor_id",
] as const;

// Active people first, then the rest, each group by name.
const byActiveThenName = (a: SupervisorOption, b: SupervisorOption) =>
  Number(b.status === ProfessionalStatus.ACTIVE) -
    Number(a.status === ProfessionalStatus.ACTIVE) ||
  a.name.localeCompare(b.name);

interface ProfessionalFiltersOnlyProps {
  // Every flagged supervisor, whatever their status, as the list response
  // returns them: an inactive one can still have people assigned.
  supervisors: SupervisorOption[];
  supervisorsLoaded: boolean;
}

export default function ProfessionalFiltersOnly({
  supervisors: supervisorList,
  supervisorsLoaded,
}: ProfessionalFiltersOnlyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const supervisors = useMemo(
    () => [...supervisorList].sort(byActiveThenName),
    [supervisorList]
  );

  // Initialize filters from URL params
  const initialFilters: ProfessionalFilters = {
    status: searchParams.get("status") || "ALL",
    job_commitment: searchParams.get("job_commitment") || "ALL",
    type: searchParams.get("type") || "ALL",
    qualification: searchParams.get("qualification")?.split(",") || [],
    is_supervisor: searchParams.get("is_supervisor") || "ALL",
    is_lead: searchParams.get("is_lead") || "ALL",
    supervisor_id: searchParams.get("supervisor_id") || "ALL",
  };

  // Initialize form with values from URL
  const form = useForm<ProfessionalFilters>({
    defaultValues: initialFilters,
  });

  // Update URL when filters change
  const applyFilters = (values: ProfessionalFilters) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", "0"); // Reset to first page when filters change

    // Keep search if it exists
    const search = searchParams.get("search");
    if (search) newParams.set("search", search);

    for (const key of SINGLE_VALUE_FILTERS) {
      if (values[key] !== "ALL") newParams.set(key, values[key]);
      else newParams.delete(key);
    }

    if (values.qualification.length > 0)
      newParams.set("qualification", values.qualification.join(","));
    else newParams.delete("qualification");

    setSearchParams(newParams);
  };

  // Reset all filters
  const handleReset = () => {
    form.reset({
      status: "ALL",
      job_commitment: "ALL",
      type: "ALL",
      qualification: [],
      is_supervisor: "ALL",
      is_lead: "ALL",
      supervisor_id: "ALL",
    });
    const newParams = new URLSearchParams();
    newParams.set("page", "0");
    // Keep search if it exists
    const search = searchParams.get("search");
    if (search) newParams.set("search", search);
    setSearchParams(newParams);
  };

  // Update form when URL changes
  useEffect(() => {
    form.reset({
      status: searchParams.get("status") || "ALL",
      job_commitment: searchParams.get("job_commitment") || "ALL",
      type: searchParams.get("type") || "ALL",
      qualification: searchParams.get("qualification")?.split(",") || [],
      is_supervisor: searchParams.get("is_supervisor") || "ALL",
      is_lead: searchParams.get("is_lead") || "ALL",
      supervisor_id: searchParams.get("supervisor_id") || "ALL",
    });
  }, [searchParams, form]);

  // A supervisor picked earlier can disappear from the list (deleted, or no
  // longer flagged), e.g. in a bookmarked URL. Radix shows a blank trigger for
  // a value it has no item for, so once the list has loaded, drop such an id
  // and fall back to "All".
  useEffect(() => {
    const id = searchParams.get("supervisor_id");
    if (!supervisorsLoaded || !id || supervisors.some((s) => s.id === id)) {
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", "0");
    newParams.delete("supervisor_id");
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, supervisors, supervisorsLoaded]);

  // Until the list has loaded, a picked supervisor has no item to show, so
  // the trigger shows the placeholder rather than a blank box.
  const supervisorValue = (value: string) =>
    (value === "ALL" || supervisorsLoaded) ? value : "";

  // Check if any filters are applied
  const areFiltersApplied =
    SINGLE_VALUE_FILTERS.some(
      (key) => !!searchParams.get(key) && searchParams.get(key) !== "ALL"
    ) || !!searchParams.get("qualification");

  return (
    <Form {...form}>
      <div className="flex w-full items-start gap-4">
        {/* Equal-width columns of at least 190px: a filter that wraps keeps
            the same width and lines up under the first column, rather than
            stretching across the whole row. */}
        <div className="grid flex-1 grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-4">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem className="min-w-0">
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), status: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    {Object.entries(professionalStatusLabels).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="job_commitment"
            render={({ field }) => (
              <FormItem className="min-w-0">
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), job_commitment: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Job Commitment" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Commitments</SelectItem>
                    {Object.entries(jobCommitmentLabels).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="min-w-0">
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), type: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Professional Type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Types</SelectItem>
                    {/* Options show the exact DB enum values (ProfessionalType) */}
                    {Object.values(ProfessionalType).map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_supervisor"
            render={({ field }) => (
              <FormItem className="min-w-0">
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), is_supervisor: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Is Supervisor" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">Is Supervisor: All</SelectItem>
                    <SelectItem value="true">Is Supervisor: Yes</SelectItem>
                    <SelectItem value="false">Is Supervisor: No</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_lead"
            render={({ field }) => (
              <FormItem className="min-w-0">
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), is_lead: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Is Lead" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">Is Lead: All</SelectItem>
                    <SelectItem value="true">Is Lead: Yes</SelectItem>
                    <SelectItem value="false">Is Lead: No</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="supervisor_id"
            render={({ field }) => (
              <FormItem className="min-w-0">
                <Select
                  value={supervisorValue(field.value)}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), supervisor_id: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Supervisor" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Supervisors</SelectItem>
                    {supervisors.map((supervisor) => (
                      <SelectItem key={supervisor.id} value={supervisor.id}>
                        <PersonOption person={supervisor} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

        {/* Qualifications */}
        {/* <FormField
          control={form.control}
          name="qualification"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <MultiSelectTags
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), qualification: value });
                  }}
                  placeholder="Enter qualification and press Enter"
                />
              </FormControl>
            </FormItem>
          )}
        /> */}
        </div>

        {areFiltersApplied && (
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="shrink-0"
          >
            <X className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>
    </Form>
  );
}

